import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Row, Col, Typography, Card, Spin, message, Button } from 'antd';
import { useQuery } from '@apollo/client';
import RecentArticlesList from '../../componenents/Lists/RecentArticlesList';
import Container from '../../componenents/Container';
import HeroImage from '../../componenents/Images/HeroImage';
import { FIND_UNIQUE_ARTICLE, updateArticleService } from '../../services/ArticleService';
import { createLike, deleteLike, getUserLikes } from '../../services/LikeService';
import ArticleAttachments from '../../componenents/Cards/ArticleAttachmentCard';
import TagCard from '../../componenents/Cards/TagCard';
import BackButton from '../../componenents/Buttons/BackButton';

const { Title } = Typography;

const AdminViewArticlePage = () => {
  const { id } = useParams();
  const [localViews, setLocalViews] = useState(null);
  const [liked, setLiked] = useState(false);
  const [likeId, setLikeId] = useState(null);
  const [isLikeLoading, setIsLikeLoading] = useState(false); // ✨ IMPROVEMENT: State for button loading
  const [localLikeCount, setLocalLikeCount] = useState(0);
  // GraphQL query to fetch the article details
  const { loading, error, data } = useQuery(FIND_UNIQUE_ARTICLE, {
    variables: { id: parseInt(id, 10) },
  });

  const article = data?.findUniqueArticle;

  // 🔥 Increment views once article is loaded
  useEffect(() => {
    if (article && localViews === null) {
      const newViews = article.views + 1;
      setLocalViews(newViews); // Immediately update locally

      updateArticleService(article.id, { views: { set: newViews } })
        .catch(err => {
          console.error('Failed to increment views:', err);
          message.warning('Failed to update views on the server.');
        });
    }
  }, [article, localViews]);

  // ✅ FIXED: Effect to check if the article is liked by the current user
  useEffect(() => {
    const userId = localStorage.getItem('userId');

    // GUARD CLAUSE: Only run if we have an article AND a logged-in user.
    if (!article || !userId) {
      return;
    }

    setLocalLikeCount(article.likes?.length || 0);
    
    const currentUserId = parseInt(userId, 10);

    const checkIfLiked = async () => {
      try {
        const likes = await getUserLikes(currentUserId, article.id);
        if (likes.length > 0) {
          setLiked(true);
          setLikeId(likes[0].id);
        } else {
          setLiked(false);
          setLikeId(null);
        }
      } catch (err) {
        console.error("Failed to check like status:", err);
        message.error("Could not verify if you liked this article.");
      }
    };

    checkIfLiked();
  }, [article]); // Dependency array only needs `article`

  // ✅ FIXED: Like toggle handler with loading state and error handling
  const handleLikeToggle = async () => {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      message.error("You must be logged in to like an article.");
      return;
    }

    setIsLikeLoading(true);
    const currentUserId = parseInt(userId, 10);

    try {
      if (liked) {
        await deleteLike(likeId);
        setLiked(false);
        setLikeId(null);
        setLocalLikeCount(prevCount => prevCount - 1); // DECREMENT
      } else {
        const newLike = await createLike(currentUserId, article.id);
        setLiked(true);
        setLikeId(newLike.id);
        setLocalLikeCount(prevCount => prevCount + 1); // INCREMENT
      }
    } catch (err) {
      console.error("Failed to toggle like:", err);
      message.error("Something went wrong. Please try again.");
    } finally {
      setIsLikeLoading(false);
    }
  };

  if (loading) return <Spin tip="Loading article..." />;
  if (error) return <p>Error loading article: {error.message}</p>;
  if (!article) return <p>Article not found</p>;

  const imageUrl = article.coverUrl ? article.coverUrl : '/default.jpg';

  return (
    <Container>
      <div style={{ padding: '10px' }}>
        <div className="back-button">
          <BackButton />
        </div>
        <Row gutter={16}>
          <Col xs={24} md={16}>
            <Card style={{ backgroundColor: '#fff', marginBottom: "20px" }}>
              <HeroImage imageUrl={imageUrl} />
              <Title>{article.title}</Title>
              <p>Published on: {new Date(article.createdAt).toLocaleDateString()}</p>
              {/* ✨ IMPROVEMENT: Use localViews for immediate UI update and safer like count */}
              <p>Views: {localViews ?? article.views} | Likes: {localLikeCount}</p>
              
              <Button
                type={liked ? 'primary' : 'default'}
                onClick={handleLikeToggle}
                loading={isLikeLoading} // Use loading state
                style={{
                  marginTop: 8,
                  borderRadius: 8,
                  fontWeight: liked ? 700 : 400,
                }}
              >
                {liked ? '♥ Liked' : '♡ Like'}
              </Button>
              
              <div
                dangerouslySetInnerHTML={{ __html: article.content }}
                style={{ marginTop: '20px', padding: '10px', border: '1px solid #f0f0f0', borderRadius: '8px', textAlign:'justify' }}
              />
            </Card>
          </Col>
          
          <Col xs={24} md={8}>
            <ArticleAttachments attachments={article.ArticleAttachment} />
            <TagCard tags={article.tags} />
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default AdminViewArticlePage;